import getFlagCompleteHelper from '../utils/getFlagCompleteHelper';
import { getIconClassName } from '@uifabric/styling/lib/index';
import { DefaultPalette } from '@uifabric/styling/lib/styles';
import { observer } from 'mobx-react';
import { getIsBitSet, ListViewBitFlagsMasks } from 'owa-bit-flags/lib/utils/listViewBitFlagsUtil';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isGroupTableQuery } from 'owa-group-common';
import { resetFocus } from 'owa-mail-focus-manager';
import { MailIcons } from 'owa-mail-icons';
import { getPropertyIconStyle, PropertyIcons } from 'owa-mail-list-actions';
import getItemClassIcon from 'owa-mail-list-actions/lib/utils/conversationProperty/getItemClassIcon';
import { listViewStore } from 'owa-mail-list-store';
import { isSnoozeScheduleEnabled } from 'owa-mail-list-store/lib/utils/snoozeUtils';
import { lazyMarkItemsAsReadBasedOnNodeIds } from 'owa-mail-mark-read-actions';
import ClientItem from 'owa-mail-store/lib/store/schema/ClientItem';
import { getItemToShowFromNodeId } from 'owa-mail-store/lib/utils/conversationsUtils';
import { lazyDeleteItemsBasedOnNodeIds } from 'owa-mail-triage-action';
import * as lazyTriageActions from 'owa-mail-triage-action';
import { HoverActionKey } from 'owa-outlook-service-options';
import FlagStatus from 'owa-service/lib/contract/FlagStatus';
import Message from 'owa-service/lib/contract/Message';
import { isConsumer } from 'owa-session-store';
import getIconForSmimeType from 'owa-smime/lib/utils/getIconForSmimeType';
import getSmimeType from 'owa-smime/lib/utils/getSmimeType';
import isSMIMEItem from 'owa-smime/lib/utils/isSMIMEItem';
import strings from 'owa-strings/owa-mail-strings';
import * as React from 'react';
import IconBar, {
    HoverActionIconProps /*PropertyIconProps*/,
    PropertyIconProps,
} from './shared/IconBar';
import {
    getHoverSurfaceAction,
    getStore as getHoverSurfaceActionStore,
} from 'owa-surface-actions-option';

const styles = require('./MailListItemPart.scss');
const classNames = require('classnames/bind').bind(styles);

export interface MailListItemPartIconBarProps {
    nodeId: string;
    tableViewId: string;
    isSingleLine: boolean;
    tableViewSupportsFlagging: boolean;
}

/*
Renders the mail list item part icon bar for both action icons and property icons
*/
@observer
export default class MailListItemPartIconBar extends React.Component<
    MailListItemPartIconBarProps,
    {}
> {
    private getHoverActionIcons(): HoverActionIconProps[] {
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        const item: Message = getItemToShowFromNodeId(this.props.nodeId);
        const canDelete = !isGroupTableQuery(tableView.tableQuery);

        const hoverActions = [];
        let hoverActionKeys: HoverActionKey[] = getHoverSurfaceActionStore().hoverSurfaceActions;

        // If no values exist in the store OR we have disabled customHoverActions.
        // Read values from owa userConfig.
        if (hoverActionKeys === null || !isFeatureEnabled('tri-customHoverActions')) {
            hoverActionKeys = getHoverSurfaceAction();
        }
        hoverActionKeys.forEach(action => {
            switch (action) {
                case 'Delete':
                    if (canDelete) {
                        hoverActions[2] = {
                            title: strings.deleteItem,
                            key: 'deleteIcon',
                            iconProps: {
                                className: getIconClassName(ControlIcons.Delete),
                            },
                            onClickCommand: this.deleteRow,
                        };
                    }
                    break;
                case 'ReadUnread':
                    hoverActions[3] = {
                        title: item.IsRead ? strings.markAsUnread : strings.markAsRead,
                        key: 'markReadIcon',
                        iconProps: {
                            className: getIconClassName(
                                item.IsRead ? ControlIcons.Mail : MailIcons.Read
                            ),
                        },
                        onClickCommand: () => this.toggleMarkAsReadRow(item),
                    };
                    break;
                case 'FlagUnflag':
                    if (this.props.tableViewSupportsFlagging) {
                        hoverActions[4] = this.getFlagCompleteEntry(item);
                    }
                    break;
                case 'None':
                    break;
            }
        });
        return hoverActions;
    }

    private getFlagCompleteEntry(item: Message) {
        const flagStatus = item.Flag.FlagStatus;

        const flagCompleteEntry = {
            title: null,
            key: null,
            iconProps: null,
            onClickCommand: () => this.toggleFlaggedStateRow(item),
        };
        const isComplete = flagStatus == 'Complete';
        const isFlagged = flagStatus == 'Flagged';
        return getFlagCompleteHelper(flagCompleteEntry, isFlagged, isComplete);
    }

    private deleteRow = async () => {
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        const deleteItemsBasedOnNodeIds = await lazyDeleteItemsBasedOnNodeIds.import();

        deleteItemsBasedOnNodeIds([this.props.nodeId], tableView.id, 'Hover');

        // Reset focus after the lazy action is complete, to ensure that focus is set
        // after any confirm dialogs are dismissed and the stitch has been raised
        resetFocus();
    };

    private toggleMarkAsReadRow = async (item: Message) => {
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        const markItemsAsReadBasedOnNodeIds = await lazyMarkItemsAsReadBasedOnNodeIds.import();

        markItemsAsReadBasedOnNodeIds(
            [this.props.nodeId],
            tableView.id,
            !item.IsRead /* isReadValueToSet */,
            true /* isExplicit */,
            'Hover'
        );

        // Reset focus after the stitch to mark the item as read is raised
        resetFocus();
    };

    private toggleFlaggedStateRow = (item: Message) => {
        let flagStatusValueToSet: FlagStatus;

        switch (item.Flag.FlagStatus) {
            case 'Complete':
            case 'NotFlagged':
                flagStatusValueToSet = 'Flagged';
                break;
            case 'Flagged':
                flagStatusValueToSet = 'NotFlagged';
                break;
        }

        lazyTriageActions.lazySetItemsFlagStateFromItemIds.importAndExecute(
            [item.ItemId.Id],
            null,
            { FlagStatus: flagStatusValueToSet },
            this.props.tableViewId,
            'Hover'
        );

        // Reset focus after the stitch to flag the item is raised
        resetFocus();
    };

    private getPropertyIconProps(): PropertyIconProps[] {
        const propertyIconProps: PropertyIconProps[] = [];
        const item: Message = getItemToShowFromNodeId(this.props.nodeId);
        const itemClassIcon = getItemClassIcon(
            [item.ItemClass],
            item.IconIndex,
            false /* hasIrm */
        );
        const flagStatus = item.Flag.FlagStatus;

        // Build the property icon list from left to right
        // Item class icon first (meeting, note, etc.)
        if (itemClassIcon && itemClassIcon != PropertyIcons.None) {
            propertyIconProps.push({
                iconClasses: getPropertyIconStyle(itemClassIcon),
                key: itemClassIcon.toString(),
            });
        }

        // Importance icon
        if (item.Importance == 'Low') {
            propertyIconProps.push({
                iconClasses: getPropertyIconStyle(PropertyIcons.ImportanceLow),
                key: PropertyIcons.ImportanceLow.toString(),
            });
        } else if (item.Importance == 'High') {
            propertyIconProps.push({
                iconClasses: getPropertyIconStyle(PropertyIcons.ImportanceHigh),
                key: PropertyIcons.ImportanceHigh.toString(),
            });
        }

        // S/MIME icon
        if (isFeatureEnabled('rp-smime') && isSMIMEItem(item)) {
            const smimeIcon: MailIcons | ControlIcons = getIconForSmimeType(
                getSmimeType(item as ClientItem)
            );

            smimeIcon &&
                propertyIconProps.push({
                    iconClasses: getIconClassName(smimeIcon),
                    key: smimeIcon,
                });
        }

        // Attachment icon
        if (item.HasAttachments) {
            propertyIconProps.push({
                iconClasses: getPropertyIconStyle(PropertyIcons.Attachment),
                key: PropertyIcons.Attachment.toString(),
            });
        }

        // At mention icon
        if (item.MentionedMe) {
            propertyIconProps.push({
                iconClasses: getPropertyIconStyle(PropertyIcons.GlobalMentionedMe),
                key: PropertyIcons.GlobalMentionedMe.toString(),
            });
        }

        // If the conversation/item has like count greater than 0, then show the LikeIcon
        if (isFeatureEnabled('rp-like') && !isConsumer() && item.LikeCount && item.LikeCount > 0) {
            propertyIconProps.push({
                iconClasses: getPropertyIconStyle(PropertyIcons.Like),
                key: PropertyIcons.Like.toString(),
            });
        }

        // Flag/Complete icon
        if (this.props.tableViewSupportsFlagging) {
            if (flagStatus == 'Flagged') {
                const flagIconClasses = classNames(
                    styles.flagIcon,
                    getIconClassName(ControlIcons.Flag)
                );
                propertyIconProps.push({
                    iconClasses: flagIconClasses,
                    key: 'flagIcon',
                });
            } else if (isFeatureEnabled('tri-flag-complete') && flagStatus == 'Complete') {
                const flagIconClasses = classNames(getIconClassName(ControlIcons.CheckMark));
                propertyIconProps.push({
                    iconClasses: flagIconClasses,
                    key: 'completeIcon',
                });
            }
        }

        // If the conversation/item has been snoozed, then show the clock icon
        if (isSnoozeScheduleEnabled() && item.ReturnTime) {
            propertyIconProps.push({
                iconClasses: getPropertyIconStyle(PropertyIcons.Snooze),
                key: PropertyIcons.Snooze.toString(),
            });
        }

        return propertyIconProps;
    }

    render() {
        const isSenderImageOff = getIsBitSet(ListViewBitFlagsMasks.HideSenderImage);
        const containerClassNames = classNames(
            this.props.isSingleLine
                ? isSenderImageOff
                    ? styles.iconBarSingleLineViewContainerNoSenderImage
                    : styles.iconBarSingleLineViewContainer
                : styles.iconBarThreeColumnContainer,
            DefaultPalette.neutralLight
        );

        return (
            <div className={containerClassNames}>
                <IconBar
                    hoverActionIcons={this.getHoverActionIcons()}
                    propertyIcons={this.getPropertyIconProps()}
                    iconClass={styles.icon}
                    propertyIconClass={styles.propertyIcon}
                    hoverIconClass={styles.hoverActionIcon}
                />
            </div>
        );
    }
}
